import { Grid } from '@mui/material';
import axios from 'axios';
import { useCallback, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import useToast from 'hooks/useToast';
import {
  IFileCabinetUploadURLResponse,
  useLazyGetFileCabinetUploadURlQuery,
  useUploadFileCabinetFileMutation,
} from 'redux-store/api/file-cabinet';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import {
  addUpload,
  failUpload,
  finishUpload,
  updateUploadProgress,
} from 'redux-store/slices/file-cabinet-upload-slice';

type DragAndDropWrapperProps = {
  children: React.ReactNode;
  fromModal?: boolean;
  projectId?: number;
  projectFolderId?: number;
};

export const DragAndDropWrapper = ({
  children,
  fromModal,
  projectId,
  projectFolderId,
}: DragAndDropWrapperProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const { folderId } = useParams();
  const { hash, pathname } = useLocation();

  const dispatch = useAppDispatch();
  const showToast = useToast();

  const [postFileToCabinet] = useUploadFileCabinetFileMutation();
  const [getUploadUrl] = useLazyGetFileCabinetUploadURlQuery();

  const resolveFolderId = useCallback((): number => {
    if (folderId) return Number(folderId);
    if (fromModal && hash) return Number(hash.substring(1));
    if (fromModal && projectFolderId) return projectFolderId;

    return 0;
  }, [folderId, hash, fromModal, projectFolderId]);

  const uploadFileWithProgress = async ({
    file,
    uploadData,
    tempId,
    folderId,
    projectId,
  }: {
    file: File;
    uploadData: IFileCabinetUploadURLResponse;
    tempId: string;
    folderId: number;
    projectId?: number;
  }) => {
    try {
      await axios.put(uploadData.url, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: ({ loaded, total }) => {
          if (!total) return;

          dispatch(
            updateUploadProgress({
              tempId,
              progress: Math.round((loaded * 100) / total),
            })
          );
        },
      });

      await postFileToCabinet({
        folderId,
        fileId: uploadData.id,
        projectId,
      }).unwrap();

      dispatch(finishUpload({ tempId }));
    } catch {
      dispatch(failUpload({ tempId }));
      showToast({ type: 'error', message: 'Something went wrong!' });
    }
  };

  const handleFilesUpload = async (files: File[]) => {
    const parentFolderId = resolveFolderId();

    await Promise.all(
      files.map(async (file) => {
        const tempId = uuid();

        dispatch(
          addUpload({
            tempId,
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            progress: 0,
            status: 'uploading',
            projectId: Number(projectId),
            folderId: String(parentFolderId),
          })
        );

        try {
          const uploadData = await getUploadUrl({
            folderId: parentFolderId,
            key: file.name,
            mimeType: file.type,
          }).unwrap();

          await uploadFileWithProgress({
            file,
            uploadData,
            tempId,
            folderId: parentFolderId,
            projectId,
          });
        } catch {
          dispatch(failUpload({ tempId }));
          showToast({ type: 'error', message: 'Something went wrong!' });
        }
      })
    );
  };

  if (pathname.includes('file-cabinet') || !hash) {
    return <>{children}</>;
  }

  return (
    <Grid
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        const nextTarget = e.relatedTarget as Node | null;
        if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
          setIsDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length) handleFilesUpload(files);
      }}
      sx={{
        width: '100%',
        height: 'calc(100% - 60px)',
        borderRadius: isDragging ? 2 : '0px',
        border: isDragging ? '2px dashed #1976d2' : '2px dashed transparent',
        transition: isDragging ? 'border 0.2s ease' : '',
      }}
    >
      {children}
    </Grid>
  );
};
